import { useEffect, useRef, useState } from "react";
import type { ReceiptClaimantOut } from "../lib/schemas";
import { formatMoney } from "../lib/format";

interface Props {
  item: { name: string; price: string };
  quantity: number;
  group?: string;
  claimed: boolean;
  claimedBy: ReceiptClaimantOut[];
  muted: boolean;
  phantomCount: number;
  myDbId: number | null;
  myPortion: number | null;
  onToggleClaim: () => void;
  onMute: () => void;
  onUnmute: () => void;
  onGuestChange: (count: number) => void;
  onSetPortion: (portion: number | null) => void;
  currency: string;
  readOnly?: boolean;
}

function parsePortion(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const slash = s.indexOf("/");
  if (slash !== -1) {
    const num = parseFloat(s.slice(0, slash));
    const den = parseFloat(s.slice(slash + 1));
    if (isNaN(num) || isNaN(den) || den === 0) return null;
    return num / den;
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

export default function ReceiptItemCard({
  item,
  quantity,
  group,
  claimed,
  claimedBy,
  muted,
  phantomCount,
  myDbId,
  myPortion,
  onToggleClaim,
  onMute,
  onUnmute,
  onGuestChange,
  onSetPortion,
  currency,
  readOnly = false,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [portionDraft, setPortionDraft] = useState(myPortion !== null ? String(myPortion) : "");
  const portionFocused = useRef(false);

  // Sync draft from server only while the input is not being edited
  useEffect(() => {
    if (!portionFocused.current) {
      setPortionDraft(myPortion !== null ? String(myPortion) : "");
    }
  }, [myPortion]);

  const hasClaims = claimedBy.length > 0 || phantomCount > 0;

  const totalPrice = Number(item.price);
  const unitPrice = quantity > 1 ? totalPrice / quantity : totalPrice;
  const priceDisplay =
    quantity > 1
      ? `${quantity} × ${formatMoney(unitPrice, currency)}`
      : formatMoney(item.price, currency);

  function handlePortionDecrease() {
    const current = portionDraft !== "" ? parseFloat(portionDraft) : (myPortion ?? 1);
    const next = Math.round((current - 1) * 1000) / 1000;
    if (next <= 0) {
      setPortionDraft("");
      onSetPortion(null);
    } else {
      setPortionDraft(String(next));
      onSetPortion(next);
    }
  }

  function handlePortionIncrease() {
    const current = portionDraft !== "" ? parseFloat(portionDraft) : (myPortion ?? 0);
    const next = Math.min(Math.round((current + 1) * 1000) / 1000, quantity);
    setPortionDraft(String(next));
    onSetPortion(next);
  }

  function handlePortionChange(raw: string) {
    setPortionDraft(raw);
    const n = parsePortion(raw);
    if (n !== null && n > 0) {
      onSetPortion(Math.min(n, quantity));
    }
  }

  function handlePortionBlur() {
    portionFocused.current = false;
    const n = parsePortion(portionDraft);
    if (n === null || n <= 0) {
      setPortionDraft("");
      onSetPortion(null);
    } else {
      const clamped = Math.min(n, quantity);
      // Normalise fractions to decimal on blur (e.g. "3/4" → "0.75")
      setPortionDraft(String(clamped));
      onSetPortion(clamped);
    }
  }

  // Portion display for a claimant row
  function portionLabel(c: ReceiptClaimantOut): string {
    if (c.portion === null) return quantity > 1 ? "÷ equal" : "÷";
    if (quantity > 1) return `${c.portion} of ${quantity}`;
    return String(c.portion);
  }

  // Per-claimant list (used in both editable and read-only branches)
  function renderClaimants(editable: boolean) {
    if (!hasClaims) return null;
    return (
      <div className="px-4 pb-2 space-y-1">
        {claimedBy.map((c) => {
          const isMe = editable && c.id === myDbId;
          return (
            <div key={c.id} className="flex items-center justify-between">
              <span className="text-xs text-fg truncate mr-2">{c.first_name}</span>
              {isMe ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={handlePortionDecrease}
                    disabled={portionDraft === "" && myPortion === null}
                    className="w-6 h-6 rounded-lg bg-surface-2 text-muted flex items-center justify-center active:opacity-70 transition-opacity text-sm leading-none disabled:opacity-30"
                  >
                    −
                  </button>
                  <input
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    value={portionDraft}
                    placeholder="auto"
                    onFocus={() => { portionFocused.current = true; }}
                    onChange={(e) => handlePortionChange(e.target.value)}
                    onBlur={handlePortionBlur}
                    className="w-12 text-center font-mono text-xs [font-variant-numeric:tabular-nums] bg-surface-2 rounded-lg border border-hairline text-fg px-1 py-1 focus:outline-none focus:border-fg"
                  />
                  <button
                    type="button"
                    onClick={handlePortionIncrease}
                    disabled={(myPortion ?? 0) >= quantity}
                    className="w-6 h-6 rounded-lg bg-surface-2 text-muted flex items-center justify-center active:opacity-70 transition-opacity text-sm leading-none disabled:opacity-30"
                  >
                    +
                  </button>
                  {quantity > 1 && (
                    <span className="font-sans text-[10px] text-muted ml-0.5">of {quantity}</span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted shrink-0">{portionLabel(c)}</span>
              )}
            </div>
          );
        })}
        {phantomCount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted truncate mr-2">
              +{phantomCount} guest{phantomCount > 1 ? "s" : ""}
            </span>
            <span className="text-xs text-muted shrink-0">÷ equal</span>
          </div>
        )}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div
        className={`mb-2 overflow-hidden rounded-2xl bg-surface shadow-card border ${
          muted ? "border-hairline opacity-50" : hasClaims ? "border-fg/30" : "border-hairline"
        }`}
      >
        <div className="w-full flex items-center justify-between px-4 py-3.5 text-left">
          <div className="flex-1 min-w-0 mr-2">
            <p className={`font-medium text-sm ${muted ? "text-muted" : "text-fg"}`}>
              {item.name}
            </p>
            {group && (
              <p className="text-[10px] text-muted mt-0.5">{group}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`font-mono text-sm [font-variant-numeric:tabular-nums] ${muted ? "text-muted" : "text-fg"}`}>
              {priceDisplay}
            </span>
            {muted && <span className="text-muted text-xs">Muted</span>}
          </div>
        </div>
        {!muted && renderClaimants(false)}
      </div>
    );
  }

  return (
    <div
      className={`mb-2 overflow-hidden rounded-2xl bg-surface shadow-card border transition-colors ${
        muted
          ? "border-hairline opacity-50"
          : claimed
            ? "border-fg"
            : hasClaims
              ? "border-fg/30"
              : "border-hairline"
      }`}
    >
      {/* Main row */}
      <button
        type="button"
        disabled={muted}
        onClick={onToggleClaim}
        className="w-full flex items-center justify-between px-4 py-3.5 active:bg-surface-2 transition-colors text-left"
      >
        <div className="flex-1 min-w-0 mr-2">
          <p className={`font-medium text-sm ${muted ? "text-muted" : "text-fg"}`}>
            {item.name}
          </p>
          {group && (
            <p className="text-[10px] text-muted mt-0.5">{group}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`font-mono text-sm [font-variant-numeric:tabular-nums] ${muted ? "text-muted" : "text-fg"}`}>
            {priceDisplay}
          </span>
          {!muted && (
            <span
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0 ${
                hasClaims
                  ? "border-fg bg-fg text-bg"
                  : "border-muted"
              }`}
            >
              {hasClaims && "✓"}
            </span>
          )}
          {muted && <span className="text-muted text-xs">Muted</span>}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
            className="text-muted text-base active:opacity-70 w-6 h-6 rounded-lg flex items-center justify-center"
          >
            {expanded ? "×" : "⋯"}
          </button>
        </div>
      </button>

      {/* Per-claimant portion rows */}
      {!muted && renderClaimants(true)}

      {/* Actions strip */}
      {expanded && (
        <div className="flex items-center gap-2 px-4 pb-3 border-t border-hairline pt-2">
          {muted ? (
            <button
              type="button"
              onClick={onUnmute}
              className="font-sans text-xs font-semibold rounded-lg px-3 py-1.5 bg-surface-2 text-muted active:opacity-70 transition-opacity"
            >
              🔉 Unmute
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onMute}
                className="font-sans text-xs font-semibold rounded-lg px-3 py-1.5 bg-surface-2 text-muted active:opacity-70 transition-opacity"
              >
                🔇 Mute
              </button>
              {phantomCount === 0 ? (
                <button
                  type="button"
                  onClick={() => onGuestChange(1)}
                  className="font-sans text-xs font-semibold rounded-lg px-3 py-1.5 bg-surface-2 text-muted active:opacity-70 transition-opacity"
                >
                  + Guest
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onGuestChange(Math.max(0, phantomCount - 1))}
                    className="w-6 h-6 rounded-lg bg-surface-2 text-muted flex items-center justify-center active:opacity-70 transition-opacity text-sm leading-none"
                  >
                    −
                  </button>
                  <span className="font-sans text-xs text-fg px-1">
                    {phantomCount} guest{phantomCount > 1 ? "s" : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() => onGuestChange(phantomCount + 1)}
                    className="w-6 h-6 rounded-lg bg-surface-2 text-muted flex items-center justify-center active:opacity-70 transition-opacity text-sm leading-none"
                  >
                    +
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
